import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, IndianRupee, Wrench, Settings, Box, Building2, LogOut, CalendarDays, FileText, StickyNote, Users, Activity, MessageSquare, BookOpen, UserCog, Users as UsersIcon, Landmark, CircleHelp as HelpCircle, Calendar, ShoppingBag, Bell, ShieldAlert, FolderOpen, ConciergeBell, UserCheck, Rss, ShieldCheck, Leaf, Handshake, HardHat, ClipboardList } from 'lucide-react';
import { supabase } from '../lib/supabase';
import UserProfile from './UserProfile';
import { useUserType } from '../hooks/useUserType';
import ThemeToggle from './ThemeToggle';

const Sidebar = () => {
  const navigate = useNavigate();
  const { userProfile, canEdit, canManageUsers, isResidentOnly } = useUserType();

  const navItems = [
    { icon: UsersIcon, label: 'Community Dashboard', path: '/community', requiresCommunity: true },
    { icon: Building2, label: 'Properties', path: '/properties' },
    { icon: IndianRupee, label: 'My Dues', path: '/my-dues', requiresResidentOnly: true },
    { icon: UserCog, label: 'Members', path: '/members', requiresAdmin: true },
    { icon: IndianRupee, label: 'Finances', path: '/finances', requiresEdit: true },
    { icon: Landmark, label: 'Corpus Fund', path: '/corpus-fund', requiresAdmin: true },
    { icon: ClipboardList, label: 'AGM Report', path: '/agm-report', requiresEdit: true },
    { icon: FileText, label: 'Taxes', path: '/taxes', requiresEdit: true },
    { icon: Bell, label: 'Reminders', path: '/reminders', requiresEdit: true },
    { icon: Wrench, label: 'Maintenance', path: '/maintenance', requiresEdit: true },
    { icon: Box, label: 'Equipment', path: '/equipment', requiresEdit: true },
    { icon: CalendarDays, label: 'Meetings', path: '/meetings', requiresEdit: true },
    { icon: StickyNote, label: 'Notes', path: '/notes' },
    { icon: Users, label: 'Contacts', path: '/contacts', requiresEdit: true },
    { icon: MessageSquare, label: 'Notice Board', path: '/notices', requiresCommunity: true },
    { icon: ShieldAlert, label: 'Complaints', path: '/complaints', requiresCommunity: true },
    { icon: Rss, label: 'Community Feed', path: '/feed', requiresCommunity: true },
    { icon: ConciergeBell, label: 'Facility Booking', path: '/facility-booking', requiresFacilityBooking: true },
    { icon: UserCheck, label: 'Visitor Management', path: '/visitors', requiresVisitors: true },
    { icon: ShieldCheck, label: 'Guard Management', path: '/guards', requiresGuards: true },
    { icon: BookOpen, label: 'Association Rules', path: '/rules', requiresCommunity: true },
    { icon: FolderOpen, label: 'Documents', path: '/documents', requiresCommunity: true },
    { icon: Calendar, label: 'Community Calendar', path: '/calendar', requiresCalendar: true },
    { icon: ShoppingBag, label: 'Marketplace', path: '/marketplace', requiresMarketplace: true },
    { icon: Leaf, label: 'Lawn Management', path: '/lawn', requiresLawn: true },
    { icon: Handshake, label: 'Vendor Management', path: '/vendors', requiresVendors: true },
    { icon: HardHat, label: 'Worker Management', path: '/workers', requiresWorkers: true },
    { icon: Activity, label: 'Activity Log', path: '/activity', requiresEdit: true },
    { icon: HelpCircle, label: 'How to Use', path: '/guide' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const hasCommunityFeatures = () => {
    if (userProfile?.user_type === 'apartment_owner' || userProfile?.user_type === 'apartment_admin') return true;
    return userProfile?.properties?.some(p => p.property_type === 'small_apartment') ?? false;
  };

  const hasCalendarEnabled = () => {
    return userProfile?.properties?.some((p: any) => p.calendar_enabled === true);
  };

  const hasMarketplaceEnabled = () => {
    return userProfile?.properties?.some((p: any) => p.marketplace_enabled === true);
  };

  const hasLawnEnabled = () => {
    return canManageUsers() && userProfile?.properties?.some((p: any) => p.lawn_management_enabled === true);
  };

  const hasVendorsEnabled = () => {
    return canManageUsers() && userProfile?.properties?.some((p: any) => p.vendor_management_enabled === true);
  };

  const hasWorkersEnabled = () => {
    return canManageUsers() && userProfile?.properties?.some((p: any) => p.workers_management_enabled === true);
  };

  const hasVisitorsEnabled = () => {
    return userProfile?.properties?.some((p: any) => p.visitor_management_enabled === true);
  };

  const hasFacilityBookingEnabled = () => {
    return userProfile?.properties?.some((p: any) => p.facility_booking_enabled === true);
  };

  const hasGuardsEnabled = () => {
    return canManageUsers() && userProfile?.properties?.some((p: any) => p.guard_management_enabled === true);
  };

  const filteredNavItems = navItems.filter(item => {
    if (item.requiresAdmin) {
      return canManageUsers();
    }
    if (item.requiresEdit) {
      return canEdit('general');
    }
    if (item.requiresCommunity) {
      return hasCommunityFeatures();
    }
    if ((item as any).requiresCalendar) {
      return hasCalendarEnabled();
    }
    if ((item as any).requiresMarketplace) {
      return hasMarketplaceEnabled();
    }
    if ((item as any).requiresLawn) {
      return hasLawnEnabled();
    }
    if ((item as any).requiresVendors) {
      return hasVendorsEnabled();
    }
    if ((item as any).requiresWorkers) {
      return hasWorkersEnabled();
    }
    if ((item as any).requiresVisitors) {
      return hasVisitorsEnabled();
    }
    if ((item as any).requiresFacilityBooking) {
      return hasFacilityBookingEnabled();
    }
    if ((item as any).requiresGuards) {
      return hasGuardsEnabled();
    }
    if ((item as any).requiresResidentOnly) {
      return isResidentOnly();
    }
    return true;
  });

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleNavClick = (item: any) => {
    navigate(item.path);
  };

  return (
    <div className="w-16 md:w-64 h-screen sticky top-0 flex flex-col overflow-hidden flex-shrink-0 bg-white dark:bg-[#0B1A15] border-r border-[#C8DED9] dark:border-[#1C3328]">
      <div className="p-2 md:p-4 border-b border-[#C8DED9] dark:border-[#1C3328]">
        <button
          onClick={() => navigate('/community')}
          className="hidden md:flex items-center gap-3 hover:opacity-80 transition-opacity w-full"
        >
          <img
            src="/apartmentwiselogoweb.png"
            alt="Apartment Wise Logo"
            className="h-12 w-auto"
            width="48"
            height="48"
          />
          <div className="flex flex-col items-start">
            <span className="text-base font-semibold text-[#0D2B24] dark:text-[#EDF4F2]">Apartment Wise</span>
            <span className="text-xs text-[#7BA89E]">Your society, fully managed</span>
          </div>
        </button>
        <button
          onClick={() => navigate('/community')}
          className="md:hidden flex justify-center hover:opacity-80 transition-opacity"
        >
          <img
            src="/apartmentwiselogoweb.png"
            alt="Apartment Wise Logo"
            className="h-10 w-auto"
            width="40"
            height="40"
          />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        {filteredNavItems.map((item) => {
          const isActive = window.location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => handleNavClick(item)}
              className={`group flex items-center justify-center md:justify-start gap-3 px-0 md:px-3 py-2.5 rounded-lg transition-all w-full text-left mb-0.5 ${
                isActive
                  ? 'bg-[#EDF4F2] dark:bg-[#16a085]/10'
                  : 'hover:bg-[#0D2B24]/[0.04] dark:hover:bg-white/[0.05]'
              }`}
            >
              <div className="flex-shrink-0">
                <item.icon
                  className={`w-5 h-5 ${isActive ? 'text-[#16a085]' : 'text-[#7BA89E] dark:text-[#9BBDB8]'}`}
                />
              </div>
              <span className={`hidden md:block text-sm font-medium ${isActive ? 'text-[#0D2B24] dark:text-[#EDF4F2]' : 'text-[#4A6B62] dark:text-[#9BBDB8]'}`}>
                {item.label}
              </span>
              {item.requiresEdit && !canEdit('general') && (
                <span className="hidden md:block ml-auto text-xs px-2 py-0.5 rounded text-[#9BBDB8] bg-[#0D2B24]/[0.06] dark:bg-white/[0.06]">
                  View
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="p-2 md:p-4 border-t border-[#C8DED9] dark:border-[#1C3328] space-y-2 md:space-y-3">
        <UserProfile />

        <div className="hidden md:block">
          <ThemeToggle />
        </div>
        <div className="md:hidden flex justify-center">
          <ThemeToggle compact />
        </div>

        <button
          onClick={handleLogout}
          className="group flex items-center justify-center md:justify-start gap-3 px-0 md:px-3 py-2.5 rounded-lg transition-all w-full text-[#7BA89E] hover:text-red-500 hover:bg-red-500/[0.06] dark:hover:bg-red-500/[0.08]"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className="hidden md:block text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
